import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import Button from '../../components/Button/Button';
import EventCardRow from '../../components/EventCardRow';
import Typography from '../../components/Typography';
import { theme } from '../../theme';
import FindPlanIcon from '../Icons/FindPlanIcon';
import AddIcon from '../Icons/AddIcon';
import { useTranslation } from 'react-i18next';

const UserEventsSection = ({ futureEvents, attendeeEvents, isOwnProfile }) => {
  const { t } = useTranslation('userProfile');
  const navigate = useNavigate();
  const [activeSubTab, setActiveSubTab] = useState('organized');

  const handleTabChange = (tab) => setActiveSubTab(tab);

  return (
    <EventsContainer>
      <HeaderBlock>

        <EventTitle $variant="title-4-semibold" $variantTablet="title-5-semibold" $variantMobile="body-1-semibold" as="h3">
          {t('events.title')}
        </EventTitle>
        <Tabs>
          <ButttonTab
            $variant="defaultInverse"
            className={activeSubTab === 'organized' ? 'Active' : ''}
            onClick={() => handleTabChange('organized')}
          >
            {t('events.organized')}
          </ButttonTab>
          <ButttonTab
            $variant="defaultInverse"
            className={activeSubTab === 'attendee' ? 'Active' : ''}
            onClick={() => handleTabChange('attendee')}
          >
            {isOwnProfile ? t('events.attendee') : t('events.attendeePublicMsg')}
          </ButttonTab>
        </Tabs>
      </HeaderBlock>

      <TabContentWrapper>
        {activeSubTab === 'organized' && (
          <TabContent>
            {futureEvents.length > 0 ? (
              futureEvents.map(event => (
                <EventCardRow key={event.id} event={event} />
              ))
            ) : (
              // Empty state para eventos organizados
              isOwnProfile ? (
                <EmptyState>
                  <EmptyStateHeader>
                    <Typography as="p" $variant="title-4-semibold" color={theme.colors.defaultMain} $align="center">
                      {/* No tienes eventos que mostrar. */}
                      {t('events.emptyState.noOrganized.title')}
                    </Typography>
                    <Typography as="p" $variant="body-1-medium" color={theme.colors.defaultWeak} $align="center">
                      {/* Cuando crees un evento aparecerá aquí. */}
                      {t('events.emptyState.noOrganized.description')}
                    </Typography>
                  </EmptyStateHeader>
                  <Button onClick={() => navigate('/create-event')}>
                    <AddIcon fill={theme.colors.inverseMain} />
                    {/* Crear Evento */}
                    {t('events.emptyState.noOrganized.button')}
                  </Button>
                </EmptyState>
              ) : (
                <EmptyState>
                  <EmptyStateHeader>
                    <Typography as="p" $variant="title-4-semibold" color={theme.colors.defaultMain} $align="center">
                      {/* Aún no ha creado ningún evento */}
                      {t('events.emptyState.noOrganized.publicMsg')}
                    </Typography>
                  </EmptyStateHeader>
                </EmptyState>
              )
            )}
          </TabContent>
        )}

        {activeSubTab === 'attendee' && (
          <TabContent>
            {attendeeEvents.length > 0 ? (
              attendeeEvents.map(event => (
                <EventCardRow key={event.id || event.id} event={event} />
              ))
            ) : (
              // Empty state para eventos organizados
              isOwnProfile ? (
                <EmptyState>
                  <EmptyStateHeader>
                    <Typography as="p" $variant="title-4-semibold" color={theme.colors.defaultMain} $align="center">
                      {/* Aún no estás inscrito en ningún evento */}
                      {t('events.emptyState.noAttendee.title')}
                    </Typography>
                    <Typography as="p" $variant="body-1-medium" color={theme.colors.defaultWeak} $align="center">
                      {/* Cuando te inscribas en un evento aparecerá aquí. */}
                      {t('events.emptyState.noAttendee.description')}

                    </Typography>
                  </EmptyStateHeader>
                  <Button onClick={() => navigate('/discover')}>
                    <FindPlanIcon fill={theme.colors.inverseMain} />
                    {t('events.emptyState.noAttendee.button')}

                    {/* Encuentra plan */}
                  </Button>
                </EmptyState>
              ) : (
                <EmptyState>
                  <EmptyStateHeader>
                    <Typography as="p" $variant="title-4-semibold" color={theme.colors.defaultMain} $align="center">
                      {/* Aún no se ha inscrito en ningún evento */}
                      {t('events.emptyState.noAttendee.publicMsg')}

                    </Typography>
                  </EmptyStateHeader>
                </EmptyState>
              )
            )}
          </TabContent>
        )}
      </TabContentWrapper>
    </EventsContainer>
  );
};

export default UserEventsSection;

const EventsContainer = styled.div`
 
  grid-area: 1 / 5 / 2 / 13;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-area: unset;
  }
`;

const HeaderBlock = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const Tabs = styled.div`
  display: flex;
  padding: ${({ theme }) => theme.sizing.xxs};
  align-items: flex-start;
  // gap: ${({ theme }) => theme.sizing.xs};
  border-radius: ${({ theme }) => theme.radius.xl};
  background: ${({ theme }) => theme.fill.defaultWeak};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-grow: 1;
  }
`;

const EventTitle = styled(Typography)`
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    display: none;
  }
`;

const ButttonTab = styled(Button)`
  border-radius: ${({ theme }) => theme.radius.xl};
  background-color: transparent;
  transition: all 0.3s ease-in-out;
  color: ${({ theme }) => theme.colors.defaultWeak};
  padding-left: ${({ theme }) => theme.sizing.sm};
  padding-right: ${({ theme }) => theme.sizing.sm};

  &.Active {
    background-color: ${({ theme }) => theme.fill.defaultMain};
    color: ${({ theme }) => theme.colors.defaultMain};
    box-shadow: 0px 0px 4px rgba(0,0,0,0.08), 0px 4px 12px rgba(0,0,0,0.04);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 14px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-grow: 1;
  }
`;

const TabContentWrapper = styled.div`
  padding-bottom: ${({ theme }) => theme.sizing.xxxl};
  padding-top: ${({ theme }) => theme.sizing.sm};
`;

const TabContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.md};
`;

const EmptyState = styled.div`
  align-items: center;
  align-self: stretch;
  border: 2px dashed ${({ theme }) => theme.border.defaultWeak};
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-sm, 16px);
  justify-content: center;
  padding: 80px 16px;
`;

const EmptyStateHeader = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xxs};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    max-width: 290px;
  }
`;